import React, { useEffect, useRef, useState } from 'react'
import { Accordion, Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { MATCH_AUTO } from '../../constants/matching-algorithms'
import { documentService } from '../../services/document.service'
import { permissionsService } from '../../services/permissions.service'
import { storagePathService } from '../../services/storage-path.service'
import { Document, StoragePath } from '../../types'
import { formatDate } from '../../utils/dateFormatter'
import { getMatchingAlgorithms, isPatternRequired } from '../../utils/matchingAlgorithms'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { CheckboxInput, EditDialog, FormField, SelectInput, TextInput, TextareaInput } from '../common/EditDialog'
import { PermissionsForm, PermissionsFormValue } from '../common/PermissionsForm'

interface StoragePathEditDialogProps {
  object?: StoragePath | null
  isCreating: boolean
  onSuccess: (storagePath?: StoragePath) => void
  onError: (error: any) => void
  onClose: () => void
}

interface StoragePathFormData {
  name: string
  path: string
  matching_algorithm: number
  match: string
  is_insensitive: boolean
  owner?: number | null
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

export const StoragePathEditDialog: React.FC<StoragePathEditDialogProps> = ({ object, isCreating, onSuccess, onError, onClose }) => {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<Document[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [testResult, setTestResult] = useState<string>('')
  const [testLoading, setTestLoading] = useState(false)
  const [testFailed, setTestFailed] = useState(false)
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [showPlaceholderDropdown, setShowPlaceholderDropdown] = useState(false)
  const placeholderButtonRef = useRef<HTMLButtonElement>(null)
  const pathTextareaRef = useRef<HTMLTextAreaElement>(null)

  const [formData, setFormData] = useState<StoragePathFormData>({
    name: '',
    path: '',
    matching_algorithm: MATCH_AUTO,
    match: '',
    is_insensitive: true,
    owner: null,
    set_permissions: {
      view: {
        users: [],
        groups: [],
      },
      change: {
        users: [],
        groups: [],
      },
    },
  })

  useEffect(() => {
    if (object) {
      // Map permissions from object to formData
      setFormData({
        name: object.name || '',
        path: object.path || '',
        matching_algorithm: object.matching_algorithm || MATCH_AUTO,
        match: object.match || '',
        is_insensitive: object.is_insensitive ?? true,
        owner: object.owner ?? null,
        set_permissions: object.permissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    } else {
      setFormData({
        name: '',
        path: '',
        matching_algorithm: MATCH_AUTO,
        match: '',
        is_insensitive: true,
        owner: null,
        set_permissions: {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    }
    setSelectedDocument(null)
    setTestResult('')
    setTestFailed(false)
  }, [object, isCreating])

  useEffect(() => {
    if (selectedDocument && formData.path) {
      testPath(selectedDocument)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.path])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleDocumentSearch = async (inputValue: string) => {
    if (!inputValue || inputValue.trim().length < 1) {
      setDocuments([])
      return
    }

    setSearchLoading(true)
    try {
      const response = await documentService.listFiltered(
        1,
        50,
        'created',
        true,
        [{ rule_type: 0, value: inputValue.trim() }], // FILTER_TITLE
        { truncate_content: true }
      )
      setDocuments(response.results || [])
    } catch (error) {
      setDocuments([])
    } finally {
      setSearchLoading(false)
    }
  }

  const handleDocumentSelect = (document: Document | null) => {
    setSelectedDocument(document)
    if (document && formData.path) {
      testPath(document)
    } else {
      setTestResult('')
      setTestFailed(false)
    }
  }

  const testPath = async (document: Document) => {
    if (!formData.path) return

    setTestLoading(true)
    try {
      const result = await storagePathService.testPath(formData.path, document.id)
      if (result && result.length > 0) {
        setTestResult(result)
        setTestFailed(false)
      } else {
        setTestResult('')
        setTestFailed(true)
      }
    } catch (error) {
      setTestResult('')
      setTestFailed(true)
    } finally {
      setTestLoading(false)
    }
  }

  const validateForm = (data: StoragePathFormData): string[] => {
    const errors: string[] = []

    if (!data.name || data.name.trim() === '') {
      errors.push('name:Name is required')
    }

    return errors
  }

  const getTitle = () => {
    return isCreating ? t('create_new_storage_path') : t('edit_storage_path')
  }

  const patternRequired = formData.matching_algorithm !== undefined && isPatternRequired(formData.matching_algorithm)
  const showPermissions = !object || permissionsService.currentUserOwnsObject(object)

  const placeholders = [
    { placeholder: '{{ title }}', description: t('storage_path_placeholder_title') },
    { placeholder: '{{ correspondent }}', description: t('storage_path_placeholder_correspondent') },
    { placeholder: '{{ document_type }}', description: t('storage_path_placeholder_document_type') },
    { placeholder: '{{ created }}', description: t('storage_path_placeholder_created') },
    { placeholder: '{{ created_year }}', description: t('storage_path_placeholder_created_year') },
    { placeholder: '{{ created_year_short }}', description: t('storage_path_placeholder_created_year_short') },
    { placeholder: '{{ created_month }}', description: t('storage_path_placeholder_created_month') },
    { placeholder: '{{ created_month_name }}', description: t('storage_path_placeholder_created_month_name') },
    { placeholder: '{{ created_month_name_short }}', description: t('storage_path_placeholder_created_month_name_short') },
    { placeholder: '{{ created_day }}', description: t('storage_path_placeholder_created_day') },
    { placeholder: '{{ added }}', description: t('storage_path_placeholder_added') },
    { placeholder: '{{ added_year }}', description: t('storage_path_placeholder_added_year') },
    { placeholder: '{{ added_year_short }}', description: t('storage_path_placeholder_added_year_short') },
    { placeholder: '{{ added_month }}', description: t('storage_path_placeholder_added_month') },
    { placeholder: '{{ added_month_name }}', description: t('storage_path_placeholder_added_month_name') },
    { placeholder: '{{ added_month_name_short }}', description: t('storage_path_placeholder_added_month_name_short') },
    { placeholder: '{{ added_day }}', description: t('storage_path_placeholder_added_day') },
    { placeholder: '{{ tag_list }}', description: t('storage_path_placeholder_tag_list') },
    { placeholder: '{{ asn }}', description: t('storage_path_placeholder_asn') },
    { placeholder: '{{ owner_username }}', description: t('storage_path_placeholder_owner_username') },
    { placeholder: '{{ original_name }}', description: t('storage_path_placeholder_original_name') },
    { placeholder: '{{ doc_pk }}', description: t('storage_path_placeholder_doc_pk') },
  ]

  const handlePlaceholderSelect = (placeholder: string) => {
    const currentPath = formData.path || ''
    const newPath = currentPath + placeholder
    handleInputChange('path', newPath)
    setShowPlaceholderDropdown(false)

    setTimeout(() => {
      if (pathTextareaRef.current) {
        pathTextareaRef.current.focus()
        const textarea = pathTextareaRef.current
        const cursorPos = textarea.value.length
        textarea.setSelectionRange(cursorPos, cursorPos)
      }
    }, 0)
  }

  const getFormFields = (errors?: Record<string, string>) => {
    return (
      <>
        <FormField label={t('name')} required error={errors?.name}>
          <TextInput
            value={formData.name || ''}
            onChange={(value) => handleInputChange('name', value)}
            placeholder={t('enter_name')}
            required
            autoComplete="off"
            error={errors?.name}
          />
        </FormField>

        <FormField
          label={
            <div className="d-flex align-items-center gap-2">
              <span>{t('path')}</span>
              <Dropdown show={showPlaceholderDropdown} onToggle={setShowPlaceholderDropdown}>
                <Dropdown.Toggle
                  ref={placeholderButtonRef}
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  style={{ textDecoration: 'none', lineHeight: '1' }}
                  title={t('available_placeholders') || 'Dostupni placeholder-i'}
                >
                  <i className="bi bi-code-square" style={{ fontSize: '1.1em' }}></i>
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                  <Dropdown.Header>
                    <small className="fw-bold">{t('available_placeholders') || 'Dostupni placeholder-i'}</small>
                  </Dropdown.Header>
                  {placeholders.map((item, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => handlePlaceholderSelect(item.placeholder)}
                      className="d-flex flex-column align-items-start"
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      <code className="text-primary mb-1">{item.placeholder}</code>
                      <small className="text-muted">{item.description}</small>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          }
        >
          <TextareaInput
            ref={pathTextareaRef}
            value={formData.path || ''}
            onChange={(value) => handleInputChange('path', value)}
            rows={3}
            monospace={true}
          />
          <div className="form-text text-muted mt-1">
            {t('see_documentation')}{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setShowDocsDialog(true)
              }}
              style={{ cursor: 'pointer' }}
            >
              {t('documentation')}
            </a>
            .
          </div>
        </FormField>

        <Accordion className="mb-3">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <i className="bi bi-eye me-2"></i>
              {t('preview')}
            </Accordion.Header>
            <Accordion.Body>
              <div className="card mb-2">
                <div className="card-body p-2">
                  {testLoading ? (
                    <div className="d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">{t('loading')}</span>
                      </div>
                      <span>{t('loading')}</span>
                    </div>
                  ) : testResult ? (
                    <code>{testResult}</code>
                  ) : testFailed ? (
                    <div className="text-danger">{t('path_test_failed')}</div>
                  ) : (
                    <div className="text-muted small">{t('no_document_selected')}</div>
                  )}
                </div>
              </div>

              <div className="docdot-input-select">
                <Select
                  options={documents.map((doc) => ({
                    value: doc,
                    label: `${doc.title} (${formatDate(doc.created)})`,
                  }))}
                  value={
                    selectedDocument
                      ? {
                          value: selectedDocument,
                          label: `${selectedDocument.title} (${new Date(selectedDocument.created).toLocaleDateString()})`,
                        }
                      : null
                  }
                  onChange={(option) => handleDocumentSelect(option?.value || null)}
                  onInputChange={(inputValue, { action }) => {
                    if (action === 'input-change') {
                      handleDocumentSearch(inputValue)
                    }
                  }}
                  placeholder={t('search_for_document')}
                  noOptionsMessage={({ inputValue }) => {
                    if (!inputValue || inputValue.length < 1) {
                      return t('type_to_search') || 'Kucajte za pretragu'
                    }
                    return t('no_documents_found')
                  }}
                  isLoading={searchLoading}
                  isClearable
                  isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <hr />

        <FormField label={t('matching_algorithm')}>
          <SelectInput
            value={formData.matching_algorithm}
            onChange={(value) => handleInputChange('matching_algorithm', value !== null ? parseInt(value) : MATCH_AUTO)}
            options={getMatchingAlgorithms(t)}
          />
        </FormField>

        {patternRequired && (
          <>
            <FormField label={t('matching_pattern')}>
              <TextInput
                value={formData.match || ''}
                onChange={(value) => handleInputChange('match', value)}
                placeholder={t('enter_matching_pattern')}
              />
            </FormField>

            <FormField label={t('case_insensitive')}>
              <CheckboxInput
                checked={formData.is_insensitive || false}
                onChange={(checked) => handleInputChange('is_insensitive', checked)}
                label={t('case_insensitive')}
              />
            </FormField>
          </>
        )}

        {showPermissions && (
          <PermissionsForm
            value={{
              owner: formData.owner || null,
              set_permissions: formData.set_permissions || {
                view: { users: [], groups: [] },
                change: { users: [], groups: [] },
              },
            }}
            onChange={(permissionsValue: PermissionsFormValue) => {
              setFormData(
                (prev) =>
                  ({
                    ...prev,
                    owner: permissionsValue.owner,
                    set_permissions: permissionsValue.set_permissions,
                  }) as StoragePathFormData
              )
            }}
            accordion={true}
          />
        )}
      </>
    )
  }

  return (
    <>
      <EditDialog
        object={object}
        isCreating={isCreating}
        onSuccess={onSuccess}
        onError={onError}
        onClose={onClose}
        service={storagePathService}
        formData={formData}
        getFormFields={getFormFields}
        getTitle={getTitle}
        validateForm={validateForm}
      />
      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey="storage_paths" />
    </>
  )
}
